import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Slider } from "@/components/ui/slider";
import { CosmicOrb } from "@/components/cosmic/CosmicOrb";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Reveal } from "@/components/cosmic/Reveal";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import { Play, Pause, ArrowRight, Compass, Sparkles, Orbit } from "lucide-react";
interface UniverseModel {
  id: string;
  name: string;
  subtitle: string;
  badge: {
    label: string;
    type: "observed" | "theoretical" | "speculative";
  };
  hue: "cyan" | "blue" | "violet" | "gold";
  rings: number;
  description: string;
  stats: [{
    label: string;
    value: string;
  }, {
    label: string;
    value: string;
  }, {
    label: string;
    value: string;
  }];
}
const UNIVERSE_MODELS: UniverseModel[] = [{
  id: "observable",
  name: "Our Observable Universe",
  subtitle: "Standard ΛCDM Cosmological Framework",
  badge: {
    label: "Observed physics",
    type: "observed"
  },
  hue: "cyan",
  rings: 2,
  description: "A 13.8-billion-year-old expanding spacetime continuum bounded by the cosmic particle horizon. Governed by dark energy, cold dark matter, and calibrated general relativity.",
  stats: [{
    label: "Spacetime Geometry",
    value: "Flat (Ω_k ≈ 0)"
  }, {
    label: "Expansion Mode",
    value: "Accelerating (H₀ ≈ 70 km/s/Mpc)"
  }, {
    label: "Co-moving Radius",
    value: "46.5 Billion Light-Years"
  }]
}, {
  id: "eternal-inflation",
  name: "Eternal-Inflation Bubble",
  subtitle: "Self-Reproducing Quantum Inflaton Fields",
  badge: {
    label: "Theoretical model",
    type: "theoretical"
  },
  hue: "blue",
  rings: 3,
  description: "Rapidly stretching cosmic inflation where quantum phase transitions halt decay locally, nucleating isolated pocket universes within an unending superluminal expansion matrix.",
  stats: [{
    label: "Inflaton Vacuum",
    value: "False-Vacuum Metastability"
  }, {
    label: "Bubble Nucleation",
    value: "Stochastic Quantum Tunneling"
  }, {
    label: "Domain Boundary",
    value: "Domain-Wall Horizon"
  }]
}, {
  id: "many-worlds",
  name: "Many-Worlds Branch",
  subtitle: "Universal Wavefunction Decoherence",
  badge: {
    label: "Speculative",
    type: "speculative"
  },
  hue: "violet",
  rings: 2,
  description: "Deterministic evolution governed entirely by the Schrödinger equation without wavefunction collapse. Every quantum superposition outcome instantiates non-interacting parallel branches of reality.",
  stats: [{
    label: "State Evolution",
    value: "Unitary (No Wavefunction Collapse)"
  }, {
    label: "Branching Cadence",
    value: "Continuous Macro Decoherence"
  }, {
    label: "Inter-branch Travel",
    value: "Orthogonal (Strict Zero Overlap)"
  }]
}, {
  id: "cyclic-brane",
  name: "Cyclic Brane Universe",
  subtitle: "Higher-Dimensional Collision Dynamics",
  badge: {
    label: "Speculative",
    type: "speculative"
  },
  hue: "gold",
  rings: 3,
  description: "In M-theory ekpyrotic scenarios, periodic collisions between parallel multidimensional branes trigger successive Big Bangs, generating cosmic matter without initial mathematical singularities.",
  stats: [{
    label: "Bulk Dimensions",
    value: "5D Orbifold Membrane"
  }, {
    label: "Cycle Duration",
    value: "~10¹² Years per Collision"
  }, {
    label: "Singularity Avoidance",
    value: "Finite Energy Bounce"
  }]
}];
export default function FeaturedUniverse() {
  const [activeId, setActiveId] = useState<string>("observable");
  const [orbitSpeed, setOrbitSpeed] = useState<number>(30); // Seconds per full revolution
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const prefersReduced = useReducedMotion();
  const currentModel = UNIVERSE_MODELS.find(m => m.id === activeId) || UNIVERSE_MODELS[0];
  const badgeStyles: Record<UniverseModel["badge"]["type"], string> = {
    observed: "border-primary/40 bg-primary/10 text-primary shadow-[0_0_12px_rgba(0,229,255,0.2)]",
    theoretical: "border-sky-400/40 bg-sky-400/10 text-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.2)]",
    speculative: "border-purple-400/40 bg-purple-400/10 text-purple-300 shadow-[0_0_12px_rgba(192,132,252,0.2)]"
  };
  return <section id="featured-universe" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground">
      <div className="relative z-10 max-w-[76rem] w-full mx-auto px-4 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow="PARAMETRIC OBSERVATORY" title="Featured Universe Architectures" description="Inspect simulated cosmological models from empirical spacetime to higher-dimensional hypothetical multiverses. Calibrate orbital mechanics in real time." align="center" />
        </Reveal>

        {/* Tab Selection Navigation */}
        <Reveal delay={100}>
          <div role="tablist" aria-label="Universe Cosmological Models" className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10 max-w-4xl mx-auto">
            {UNIVERSE_MODELS.map(model => {
            const isActive = model.id === activeId;
            return <button key={model.id} type="button" id={`tab-${model.id}`} role="tab" aria-selected={isActive} aria-controls={`panel-${model.id}`} tabIndex={isActive ? 0 : -1} onClick={() => setActiveId(model.id)} className={`ql-chip transition-all duration-200 text-xs sm:text-sm font-sans ${isActive ? "!bg-primary !text-primary-foreground font-semibold shadow-[0_0_16px_var(--glow-primary)] border-primary" : "hover:border-primary/50 hover:text-foreground"}`}>
                  <Orbit className={`size-3.5 transition-transform ${isActive ? "rotate-45 text-primary-foreground" : "text-primary"}`} />
                  <span>{model.name}</span>
                </button>;
          })}
          </div>
        </Reveal>

        {/* Main Stage Glass Console */}
        <Reveal delay={180}>
          <div id={`panel-${currentModel.id}`} role="tabpanel" aria-labelledby={`tab-${currentModel.id}`} className="ql-glass-strong p-4 sm:p-8 lg:p-12 relative overflow-hidden rounded-2xl w-full max-w-full">
            {/* Top specular glow rule */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" aria-hidden="true" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* Left Column (cols 1-6): Orbital Astronomical Stage */}
              <div className="lg:col-span-6 flex flex-col items-center justify-center w-full min-w-0">
                <div className="relative flex items-center justify-center w-full aspect-square max-w-[280px] min-[360px]:max-w-[320px] sm:max-w-[420px] rounded-2xl bg-card/40 border border-border/50 p-4 sm:p-8 backdrop-blur-md overflow-hidden" style={{
                "--ring-duration": `${orbitSpeed}s`
              } as React.CSSProperties}>
                  {/* Subtle background radar/celestial grid lines */}
                  <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,229,255,0.06)_1px,transparent_1px)] bg-[size:28px_28px] pointer-events-none opacity-60" />
                  <div aria-hidden="true" className="absolute inset-8 rounded-full border border-dashed border-border/40 pointer-events-none" />
                  <div aria-hidden="true" className="absolute inset-20 rounded-full border border-border/30 pointer-events-none" />

                  {/* Satellite 1 drifting along CSS variable duration */}
                  <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{
                  animation: isPlaying && !prefersReduced ? `ql-rotate var(--ring-duration) linear infinite` : "none"
                }}>
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      <span className="size-2 rounded-full bg-primary shadow-[0_0_8px_var(--glow-primary)]" />
                      <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/80 hidden sm:inline-block">
                        SAT-01
                      </span>
                    </div>
                  </div>

                  {/* Satellite 2 reverse drift */}
                  <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{
                  animation: isPlaying && !prefersReduced ? `ql-rotate-reverse calc(var(--ring-duration) * 1.5) linear infinite` : "none"
                }}>
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      <span className="size-1.5 rounded-full bg-cyan-300 shadow-[0_0_6px_var(--glow-laser)]" />
                      <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/80 hidden sm:inline-block">
                        COORD-β
                      </span>
                    </div>
                  </div>

                  {/* Central Cosmic Orb with Crossfade transition on model switch */}
                  <AnimatePresence mode="wait">
                    <motion.div key={currentModel.id} initial={{
                    scale: 0.8,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} exit={{
                    scale: 0.8,
                    opacity: 0
                  }} transition={{
                    duration: 0.35,
                    ease: "easeOut"
                  }} className="relative z-10 flex items-center justify-center scale-75 min-[360px]:scale-90 sm:scale-100 origin-center">
                      <CosmicOrb size={195} hue={currentModel.hue} rings={currentModel.rings} />
                    </motion.div>
                  </AnimatePresence>

                  {/* Lower status telemetry badge */}
                  <div className="absolute bottom-2 sm:bottom-3 inset-x-2 sm:inset-x-4 flex items-center justify-between text-[10px] sm:text-[11px] font-mono text-muted-foreground bg-card/85 border border-border/40 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 backdrop-blur-sm">
                    <span className="flex items-center gap-1.5 truncate">
                      <span className={`size-1.5 rounded-full shrink-0 ${isPlaying ? "bg-primary animate-pulse" : "bg-muted-foreground"}`} />
                      <span className="truncate">{isPlaying ? "SIMULATING" : "FROZEN"}</span>
                    </span>
                    <span className="shrink-0 ml-1">T_ROT: {orbitSpeed}s</span>
                  </div>
                </div>

                {/* Calibrator Controls: Orbit Speed Slider + Pause/Play */}
                <div className="w-full max-w-[280px] min-[360px]:max-w-[320px] sm:max-w-[420px] mt-6 p-3 sm:p-4 rounded-xl bg-card/60 border border-border/40">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Compass className="size-4 text-primary shrink-0" />
                      <span className="font-mono text-xs font-semibold uppercase text-card-foreground">
                        Orbit Velocity
                      </span>
                    </div>
                    <span className="font-mono text-xs text-primary font-bold">
                      {orbitSpeed}s / cycle
                    </span>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4 touch-none">
                    <Slider min={10} max={60} step={2} value={[orbitSpeed]} onValueChange={val => setOrbitSpeed(val[0] ?? 30)} aria-label="Orbit rotation speed duration" className="flex-1 cursor-pointer py-2 touch-none" />

                    <button type="button" onClick={() => setIsPlaying(!isPlaying)} aria-label={isPlaying ? "Pause orbit cycle" : "Resume orbit cycle"} className="ql-btn-ghost !min-h-[44px] !min-w-[44px] !p-2 rounded-full shrink-0 flex items-center justify-center">
                      {isPlaying ? <Pause className="size-4 text-primary" /> : <Play className="size-4 text-primary ml-0.5" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column (cols 7-12): Information & Metrics */}
              <div className="lg:col-span-6 flex flex-col items-start justify-center w-full min-w-0">
                <AnimatePresence mode="wait">
                  <motion.div key={currentModel.id} initial={{
                  opacity: 0,
                  x: 16
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -16
                }} transition={{
                  duration: 0.35,
                  ease: "easeOut"
                }} className="w-full flex flex-col items-start">
                    {/* Status Badge */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-mono font-semibold tracking-wide border ${badgeStyles[currentModel.badge.type]}`}>
                        <Sparkles className="size-3" />
                        <span>{currentModel.badge.label}</span>
                      </span>
                      <span className="text-xs font-mono text-muted-foreground uppercase">
                        Specimen {currentModel.id}
                      </span>
                    </div>

                    {/* Headline */}
                    <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight break-words">
                      {currentModel.name}
                    </h3>
                    <p className="font-mono text-xs uppercase tracking-wider text-primary mt-1 mb-4">
                      {currentModel.subtitle}
                    </p>

                    {/* Narrative Description */}
                    <p className="font-sans text-base text-muted-foreground leading-relaxed mb-6">
                      {currentModel.description}
                    </p>

                    {/* 3 Key Stats Module */}
                    <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                      {currentModel.stats.map((stat, i) => <div key={i} className="rounded-lg bg-card/70 border border-border/50 p-3.5 backdrop-blur-xs flex flex-col justify-between">
                          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                            {stat.label}
                          </span>
                          <span className="font-mono text-xs sm:text-[13px] font-semibold text-foreground mt-2 break-words">
                            {stat.value}
                          </span>
                        </div>)}
                    </div>

                    {/* CTA to Atlas */}
                    <Link to="/MultiverseAtlasPage" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center">
                      <span>Explore in the Atlas</span>
                      <ArrowRight className="size-4 shrink-0" />
                    </Link>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}